import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrophyIcon, 
  EyeIcon,
  PencilIcon,
  PlusIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { SeasonService, PredictionService } from '../../services/firebaseService';
import { getEliminationTypeDisplayLabel } from '../../utils/predictionUtils';
import { buildClosedWeekScoreStatusMap } from '../../utils/weekScoreStatus';

export default function AdminDashboard() {
  const { userProfile } = useAuth();
  const [activeSeason, setActiveSeason] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [closedWeeks, setClosedWeeks] = useState([]);
  const [predictionCount, setPredictionCount] = useState(0);
  const [weekScoreStatus, setWeekScoreStatus] = useState({}); // { weekNumber: { calculated: boolean, calculatedAt: timestamp } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get active season (should only be one)
      const activeSeasons = await SeasonService.getActiveSeasons();
      if (activeSeasons.length === 0) {
        setError('No active season found');
        setLoading(false);
        return;
      }
      
      const season = activeSeasons[0]; // There should only be one active season
      setActiveSeason(season);
      
      // Find open week and closed weeks
      const weeks = season.weeks || [];
      const open = weeks.find(w => w.isOpenForPredictions === true);
      const closed = weeks.filter(w => w.isOpenForPredictions === false).sort((a, b) => b.weekNumber - a.weekNumber);
      
      setOpenWeek(open);
      setClosedWeeks(closed);
      
      // Count predictions for open week
      if (open) {
        const predictions = await PredictionService.getPredictionsBySeasonAndWeek(season.id, open.weekNumber);
        setPredictionCount(predictions.length);
      }
      
      const scoreStatus = await buildClosedWeekScoreStatusMap(season.id, closed);
      setWeekScoreStatus(scoreStatus);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getWeekTypeLabel = (week) => {
    if (week.isFinale) return 'Finale';
    return getEliminationTypeDisplayLabel(week) || 'Single Elimination';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (error && !activeSeason) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500">
                Welcome back, {userProfile?.displayName}
              </p>
            </div>
          </div>
        </div>

        {activeSeason ? (
          <>
            {/* Active Season Container with nested weeks */}
            <div className="px-4 py-6 sm:px-0">
              <div className="bg-white shadow rounded-lg">
                {/* Season Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{activeSeason.name}</h2>
                      <p className="mt-1 text-sm text-gray-500">
                        Active Season • {activeSeason.contestants?.length || 0} contestants
                      </p>
                    </div>
                    <Link
                      to={`/admin/seasons/${activeSeason.id}`}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-2" />
                      View Season
                    </Link>
                  </div>
                </div>

                {/* Current Open Week */}
                {openWeek ? (
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-green-50 border-l-4 border-l-green-500">
                    <h3 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">Current Open Week</h3>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h4 className="text-xl font-bold text-gray-900">
                            Week {openWeek.weekNumber}
                          </h4>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-500 text-white shadow-sm">
                            Open for Predictions
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getWeekTypeLabel(openWeek)}
                          </span>
                        </div>
                        {openWeek.airDate && (
                          <p className="text-sm text-gray-600 mb-3">
                            Air Date: {formatDate(openWeek.airDate)}
                          </p>
                        )}
                        <div>
                          <Link
                            to={`/admin/seasons/${activeSeason.id}/week/${openWeek.weekNumber}/picks`}
                            className="inline-flex items-center text-sm font-medium text-survivor-600 hover:text-survivor-700"
                          >
                            <ChartBarIcon className="h-4 w-4 mr-1" />
                            {predictionCount} prediction{predictionCount !== 1 ? 's' : ''} submitted
                          </Link>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:ml-6 sm:flex-shrink-0">
                        <Link
                          to={`/admin/seasons/${activeSeason.id}/week/${openWeek.weekNumber}/picks`}
                          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <EyeIcon className="h-4 w-4 mr-2" />
                          View Picks
                        </Link>
                        <Link
                          to={`/admin/seasons/${activeSeason.id}/week/${openWeek.weekNumber}`}
                          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Edit Week
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="text-center py-4">
                      <p className="text-gray-500 mb-4">No week is currently open for predictions.</p>
                      <Link
                        to={`/admin/seasons/${activeSeason.id}`}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Create New Week
                      </Link>
                    </div>
                  </div>
                )}

                {/* Closed Weeks */}
                {closedWeeks.length > 0 && (
                  <div className="px-4 sm:px-6 py-4">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Closed Weeks</h3>
                    <div className="space-y-2">
                      {closedWeeks.map((week) => {
                        const scoreStatus = weekScoreStatus[week.weekNumber] || { calculated: false };
                        const hasResults = week.results && Object.keys(week.results).length > 0;
                        
                        return (
                          <div
                            key={week.weekNumber}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border border-gray-100 rounded-md bg-gray-50 hover:bg-gray-100"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h4 className="text-base font-medium text-gray-600">
                                  Week {week.weekNumber}
                                </h4>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                  {getWeekTypeLabel(week)}
                                </span>
                                {week.isFinale && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                    Finale
                                  </span>
                                )}
                              </div>
                              {week.airDate && (
                                <p className="text-xs text-gray-500 mb-2">
                                  Air Date: {formatDate(week.airDate)}
                                </p>
                              )}
                              <div className="flex flex-wrap items-center gap-3 text-xs">
                                {hasResults ? (
                                  <span className="inline-flex items-center text-gray-500">
                                    <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                                    Results entered
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center text-gray-400">
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    No results
                                  </span>
                                )}
                                {scoreStatus.calculated ? (
                                  <span className="inline-flex items-center text-gray-500">
                                    <CheckCircleIcon className="h-3 w-3 mr-1 text-green-500" />
                                    Scores calculated
                                    {scoreStatus.calculatedAt && (
                                      <span className="ml-1 text-gray-400">
                                        ({formatDateTime(scoreStatus.calculatedAt)})
                                      </span>
                                    )}
                                  </span>
                                ) : hasResults ? (
                                  <span className="inline-flex items-center text-yellow-600">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Scores not calculated
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex sm:flex-shrink-0">
                              <Link
                                to={`/admin/seasons/${activeSeason.id}/week/${week.weekNumber}/picks`}
                                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-600 bg-white hover:bg-gray-50 w-full sm:w-auto"
                              >
                                <EyeIcon className="h-3 w-3 mr-1" />
                                View Picks
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="px-4 py-6 sm:px-0">
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Link
                    to={`/admin/seasons/${activeSeason.id}`}
                    className="flex items-center p-4 border border-transparent rounded-lg text-white bg-survivor-600 hover:bg-survivor-700 shadow-sm"
                  >
                    <PlusIcon className="h-5 w-5 mr-3" />
                    <span className="text-sm font-medium">Create New Week</span>
                  </Link>
                  <Link
                    to="/admin/seasons"
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <TrophyIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-gray-900">Manage Seasons</span>
                  </Link>
                  <Link
                    to="/admin/leagues"
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <ChartBarIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-gray-900">View All Leagues</span>
                  </Link>
                  <Link
                    to="/admin/users"
                    className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <UsersIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-sm font-medium text-gray-900">Manage Users</span>
                  </Link>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6 text-center">
              <p className="text-gray-500 mb-4">No active season found.</p>
              <Link
                to="/admin/seasons/create"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Season
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
